import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 * @property {HTMLElement} cartAnnouncement - The cart announcement element.
 * @property {HTMLElement} closeButton - The close button element.
 * @property {HTMLElement} cartSummary - The cart summary element.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  constructor() {
    super();
    this.originalWalletConfigs = null;
    this.savedIdmeVerification = null;
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.#saveIdmeVerification();
    this.#correctBogoQuantities();

    setTimeout(() => {
      this.#initializePaypalLogic();
      if (!this.savedIdmeVerification) {
        this.#saveIdmeVerification();
      }
    }, 100);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  #initializePaypalLogic() {
    if (document.querySelector('shopify-accelerated-checkout-cart')) {
      this.#saveOriginalWalletConfigs();
      this.#checkCartAndUpdatePaypalButtons();
    }
  }

  #saveOriginalWalletConfigs() {
    const walletElement = document.querySelector('#dynamic-checkout-cart');
    const walletElemAttr = walletElement?.innerHTML;

    if (walletElemAttr) {
      const walletElem = walletElemAttr?.split('wallet-configs="')[1]?.split('"')[0];
      if (walletElem && this.originalWalletConfigs == null) {
        this.walletElementContainer = walletElement;
        this.originalWalletConfigs = walletElem;
      }
    }
  }

  async #checkCartAndUpdatePaypalButtons() {
    try {
      if (!this.originalWalletConfigs) {
        this.#saveOriginalWalletConfigs();
      }

      // Fetch cart data once to avoid caching issues
      const cartData = await this.#fetchCartData();

      const hasPaypalExcludeProducts = this.#checkForPaypalExcludeProducts(cartData);
      const hasAmazonPayExcludeProducts = this.#checkForAmazonPayExcludeProducts(cartData);
      const hasRestrictedItems = this.#checkForRestrictedItems(cartData);
      const hasInventoryMessageItems = this.#checkForInventoryMessageItems(cartData);
      const hasSubscriptionItems = this.#checkForSubscriptionItems(cartData);

      this.#showCheckoutElements();

      let hasAnyExcludeProducts = false;

      if (
        hasPaypalExcludeProducts ||
        hasAmazonPayExcludeProducts ||
        hasRestrictedItems ||
        hasInventoryMessageItems ||
        hasSubscriptionItems
      ) {
        this.#updateWalletButtons(
          hasPaypalExcludeProducts,
          hasAmazonPayExcludeProducts,
          hasRestrictedItems,
          hasInventoryMessageItems,
          hasSubscriptionItems
        );
        hasAnyExcludeProducts = true;
      } else {
        this.#restoreOriginalButtons();
      }

      const additionalCheckoutButtonsContainer = document
        .querySelector('shopify-accelerated-checkout-cart')
        ?.closest('.additional-checkout-buttons');
      if (additionalCheckoutButtonsContainer?.classList.contains('block-pointer-events')) {
        additionalCheckoutButtonsContainer.classList.remove('block-pointer-events');
      }
    } catch (error) {
      console.error('Error checking cart for exclude/restricted products:', error);
    }
  }

  async #fetchCartData() {
    try {
      // Small delay to ensure server has updated cart data
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Add timestamp to prevent caching
      const timestamp = Date.now();
      const response = await fetch(`/cart.js?t=${timestamp}`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      const cart = await response.json();

      return cart;
    } catch (error) {
      console.error('Error fetching cart data:', error);
      return null;
    }
  }

  #checkForPaypalExcludeProducts(cartData) {
    if (!cartData) return false;

    if (cartData.item_count === 0) {
      this.originalWalletConfigs = null;
    }

    const hasExcludedItem = cartData.items.some((item) => {
      const metafields = this.#getItemMetafieldsFromDom(item);
      if (metafields && metafields.paypal_exclude_flag) return true;
      return item.properties && item.properties._paypal_exclude_flag;
    });

    return hasExcludedItem;
  }

  #checkForAmazonPayExcludeProducts(cartData) {
    if (!cartData) return false;

    if (cartData.item_count === 0) {
      this.originalWalletConfigs = null;
    }

    const hasExcludedItem = cartData.items.some((item) => {
      const metafields = this.#getItemMetafieldsFromDom(item);
      if (metafields && metafields.amazon_pay_exclude_flag) return true;
      return item.properties && item.properties._amazon_pay_exclude_flag;
    });

    return hasExcludedItem;
  }

  #checkForRestrictedItems(cartData) {
    if (!cartData) return false;

    const hasRestrictedItem = cartData.items.some((item) => {
      const metafields = this.#getItemMetafieldsFromDom(item);
      if (metafields && metafields.item_restrict_type) return true;
      return item.properties && item.properties._item_restrict_type;
    });

    return hasRestrictedItem;
  }

  #checkForInventoryMessageItems(cartData) {
    if (!cartData) return false;

    const hasInventoryMessageItem = cartData.items.some((item) => {
      const metafields = this.#getItemMetafieldsFromDom(item);
      if (metafields && metafields.product_stock_status == 'I') return false;
      if (metafields && (metafields.inventory_message || metafields.short_inventory_message))
        return true;
      const hasInventoryMessage =
        item.properties &&
        (item.properties._inventory_message || item.properties._short_inventory_message);
      return hasInventoryMessage;
    });

    return hasInventoryMessageItem;
  }

  #checkForSubscriptionItems(cartData) {
    if (!cartData) return false;

    const hasSubscriptionItem = cartData.items.some((item) => {
      if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
        return true;
      }
      if (item.properties && item.properties._selling_plan_id) {
        return true;
      }
      return false;
    });

    return hasSubscriptionItem;
  }

  /**
   * Attempts to read metafields JSON for a given cart item from the DOM.
   * Relies on script[type="application/json"][data-cart-item-metafields] located within the cart row for that item.
   * Uses product_id and variant_id to match DOM rows when possible, otherwise falls back to index order.
   */
  #getItemMetafieldsFromDom(item) {
    try {
      const rows = Array.from(document.querySelectorAll('[ref="cartItemRows[]"]'));
      const matchingRow = rows.find((row) => {
        const rowVariantId = row.getAttribute('data-variant-id');
        const rowProductId = row.getAttribute('data-product-id');
        const matchesVariant =
          rowVariantId && item?.variant_id && String(rowVariantId) === String(item.variant_id);
        const matchesProduct =
          rowProductId && item?.product_id && String(rowProductId) === String(item.product_id);
        return Boolean(matchesVariant || matchesProduct);
      });

      if (matchingRow) {
        const script = matchingRow.querySelector('script[data-cart-item-metafields]');
        if (script && script.textContent) return JSON.parse(script.textContent);
      }

      if (item && item.url) {
        const byUrl = rows.find((row) => {
          const link = row.querySelector('a.cart-items__title, a.cart-items__media-container');
          if (link && link instanceof HTMLElement) {
            const href = link.getAttribute('href');
            return href === item.url;
          }
          return false;
        });
        if (byUrl) {
          const script = byUrl.querySelector('script[data-cart-item-metafields]');
          if (script && script.textContent) return JSON.parse(script.textContent);
        }
      }
    } catch (e) {
      console.error('Error parsing cart item metafields from DOM:', e);
    }
    return null;
  }

  #hideCheckoutElements() {
    const dynamicCheckoutCart = document.querySelector('#dynamic-checkout-cart');
    const paymentTerms = document.querySelector('shopify-payment-terms');
    const checkoutButtonText = document.querySelector('.cart__checkout-button-text');
    const acceleratedCheckoutCart = document.querySelector('shopify-accelerated-checkout-cart');

    if (dynamicCheckoutCart) {
      dynamicCheckoutCart.style.display = 'none';
    }

    if (paymentTerms) {
      paymentTerms.style.display = 'none';
    }

    if (checkoutButtonText) {
      checkoutButtonText.style.display = 'none';
    }

    if (acceleratedCheckoutCart) {
      acceleratedCheckoutCart.style.display = 'none';
    }
  }

  #showCheckoutElements() {
    const dynamicCheckoutCart = document.querySelector('#dynamic-checkout-cart');
    const paymentTerms = document.querySelector('shopify-payment-terms');
    const checkoutButtonText = document.querySelector('.cart__checkout-button-text');
    const acceleratedCheckoutCart = document.querySelector('shopify-accelerated-checkout-cart');

    if (dynamicCheckoutCart) {
      dynamicCheckoutCart.style.display = '';
    }

    if (paymentTerms) {
      paymentTerms.style.display = '';
    }

    if (checkoutButtonText) {
      checkoutButtonText.style.display = '';
    }

    if (acceleratedCheckoutCart) {
      acceleratedCheckoutCart.style.display = '';
    }
  }

  /**
   * Updates the wallet buttons, removing PayPal, Amazon Pay, Apple Pay and/or Google Pay depending on the exclusion flags
   * @param {boolean} hasPaypalExcludeProducts - flag for PayPal exclusion
   * @param {boolean} hasAmazonPayExcludeProducts - flag for Amazon Pay exclusion
   * @param {boolean} hasRestrictedItems - flag for Apple Pay and Google Pay exclusion
   * @param {boolean} hasInventoryMessageItems - flag for inventory message items
   * @param {boolean} hasSubscriptionItems - flag for subscription/Autoship items
   */
  #updateWalletButtons(
    hasPaypalExcludeProducts,
    hasAmazonPayExcludeProducts,
    hasRestrictedItems = false,
    hasInventoryMessageItems = false,
    hasSubscriptionItems = false
  ) {
    const walletElement = document.querySelector('#dynamic-checkout-cart');
    const walletElemAttr = walletElement?.innerHTML;
    this.#saveOriginalWalletConfigs();

    if (!walletElemAttr || !this.originalWalletConfigs) {
      return;
    }

    try {
      const updatedWalletElemAttr = walletElement.innerHTML;

      const walletElemClear = this.originalWalletConfigs.replace(/&quot;/g, '"');
      const jsonWallet = JSON.parse(walletElemClear);

      /** @type {string[]} */
      const buttonsToRemove = [];
      if (hasPaypalExcludeProducts) {
        buttonsToRemove.push('paypal');
      }
      if (hasAmazonPayExcludeProducts) {
        buttonsToRemove.push('amazon_pay');
      }
      if (hasRestrictedItems) {
        buttonsToRemove.push('apple_pay');
        buttonsToRemove.push('google_pay');
      }
      if (hasInventoryMessageItems) {
        buttonsToRemove.push('apple_pay');
        buttonsToRemove.push('google_pay');
        buttonsToRemove.push('paypal');
        buttonsToRemove.push('amazon_pay');
      }
      if (hasSubscriptionItems) {
        buttonsToRemove.push('amazon_pay');
      }

      if (buttonsToRemove.length === 0) {
        return;
      }

      const filteredWallet = jsonWallet.filter((item) => !buttonsToRemove.includes(item.name));

      if (filteredWallet.length === 0) {
        const checkoutButtonText = document.querySelector('.cart__checkout-button-text');
        if (checkoutButtonText) {
          checkoutButtonText.style.display = 'none';
        }
        return;
      }

      const newWalletStr = JSON.stringify(filteredWallet).replace(/"/g, '&quot;');
      const newWallStr = updatedWalletElemAttr.replace(this.originalWalletConfigs, newWalletStr);

      walletElement.innerHTML = newWallStr;
    } catch (error) {
      console.error('Error updating wallet buttons:', error);
    }
  }

  #restoreOriginalButtons() {
    const walletElement = document.querySelector('#dynamic-checkout-cart');
    const walletElemAttr = walletElement?.innerHTML;

    if (!walletElemAttr || !this.originalWalletConfigs) {
      return;
    }

    if (walletElemAttr.includes(this.originalWalletConfigs)) {
      return;
    }

    try {
      const currentWalletElem = walletElemAttr.split('wallet-configs="')[1]?.split('"')[0];

      if (currentWalletElem && currentWalletElem !== this.originalWalletConfigs) {
        const restoredWallStr = walletElemAttr.replace(
          currentWalletElem,
          this.originalWalletConfigs
        );
        walletElement.innerHTML = restoredWallStr;
      }
    } catch (error) {
      console.error('Error restoring original buttons:', error);
    }
  }

  /**
   * Updates the additional-checkout-buttons container from the new section HTML.
   * This is needed because shopify-accelerated-checkout-cart is skipped during morphing.
   * @param {string} sectionHtml - The new HTML of the section
   */
  #updateAdditionalCheckoutButtons(sectionHtml) {
    try {
      const sectionDoc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const newAdditionalCheckoutButtons = sectionDoc.querySelector('.additional-checkout-buttons');
      const existingAdditionalCheckoutButtons = document.querySelector(
        '.additional-checkout-buttons'
      );

      if (newAdditionalCheckoutButtons && existingAdditionalCheckoutButtons) {
        existingAdditionalCheckoutButtons.innerHTML = newAdditionalCheckoutButtons.innerHTML;

        this.originalWalletConfigs = null;
        this.#saveOriginalWalletConfigs();
      }
    } catch (error) {
      console.error('Error updating additional checkout buttons:', error);
    }
  }

  #saveIdmeVerification() {
    const idmeElement = document.querySelector('#idme-verification');
    if (idmeElement && !this.savedIdmeVerification) {
      const idmeButton = idmeElement.querySelector('.idme-trigger-link.idme-unify-button');
      if (idmeButton) {
        this.savedIdmeVerification = idmeElement.outerHTML;
      } else {
        this.#waitForIdmeButton(idmeElement);
      }
    } else if (idmeElement && this.savedIdmeVerification) {
      const idmeButton = idmeElement.querySelector('.idme-trigger-link.idme-unify-button');
      if (idmeButton) {
        this.savedIdmeVerification = idmeElement.outerHTML;
      }
    }
  }

  /**
   * Handles cart errors where Shopify may still partially apply updates (availability caps).
   * Syncs cart UI/count and normalizes "Only X items..." message based on actual delta.
   * @param {number} line
   * @param {number} requestedQuantity
   * @param {string} backendMessage
   */
  #handleCartErrorAndRefresh = async (line, requestedQuantity, backendMessage) => {
    // NOTE: Shopify's availability error message reports the final quantity ("Only 3 items...")
    // which can be misleading when the customer already had some quantity in cart.
    // We normalize the message using the actual cart delta and render the error ONCE.
    let normalizedMessage = backendMessage;
    let itemCount = null;

    try {
      const row = this.refs.cartItemRows?.[line - 1];
      const lineKey = row?.dataset?.lineKey;

      const previousQuantity =
        Number(row?.dataset?.lineQuantity) ||
        Number(row?.querySelector('input')?.defaultValue) ||
        Number(row?.querySelector('input')?.value) ||
        0;

      const response = await fetch(`${Theme.routes.cart_url}.js?t=${Date.now()}`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      if (response.ok) {
        const cartData = await response.json();
        itemCount = Number(cartData.item_count) || 0;

        let updatedItem = null;
        if (lineKey) {
          updatedItem = cartData.items.find((item) => String(item.key) === String(lineKey));
        }
        if (!updatedItem) {
          updatedItem = cartData.items[line - 1] || null;
        }

        const actualQuantity = Number(updatedItem?.quantity) || 0;
        const requested = Number(requestedQuantity) || 0;

        const requestedAdded = Math.max(0, requested - previousQuantity);
        const actualAdded = Math.max(0, actualQuantity - previousQuantity);

        if (requestedAdded > 0 && actualAdded < requestedAdded) {
          normalizedMessage =
            actualAdded === 0
              ? 'No items were added to your cart due to availability.'
              : `Only ${actualAdded} more ${actualAdded === 1 ? 'item was' : 'items were'} added to your cart due to availability.`;
        }
      }

      // Force section refresh so row qty, totals, etc. match server truth
      await sectionRenderer.renderSection(this.sectionId, { cache: false });

      if (typeof itemCount === 'number') {
        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount,
            source: 'cart-items-component-refresh-after-error',
            sections: {},
          })
        );
      }
    } catch (error) {
      console.error('Error syncing cart after availability error:', error);
    } finally {
      // Show error after section refresh (DOM refs replaced). Render ONCE with normalized message.
      this.#handleCartError(line, { errors: normalizedMessage });

      setTimeout(() => {
        this.#checkCartAndUpdatePaypalButtons();
        this.#restoreIdmeVerification();
        this.#toggleWishlistButtonClass();
      }, 150);
    }
  };

  #waitForIdmeButton(idmeElement) {
    const observer = new MutationObserver((mutations) => {
      const idmeButton = idmeElement.querySelector('.idme-trigger-link.idme-unify-button');
      if (idmeButton && !this.savedIdmeVerification) {
        this.savedIdmeVerification = idmeElement.outerHTML;
        observer.disconnect();
      }
    });

    observer.observe(idmeElement, {
      childList: true,
      subtree: true,
    });

    setTimeout(() => {
      if (!this.savedIdmeVerification) {
        observer.disconnect();
      }
    }, 10000);
  }

  #restoreIdmeVerification() {
    if (this.savedIdmeVerification) {
      const idmeElement = document.querySelector('#idme-verification');
      if (idmeElement) {
        idmeElement.outerHTML = this.savedIdmeVerification;
      }
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    const lineItemRow = this.refs.cartItemRows[line - 1];
    const lines = [line];

    if (lineItemRow) {
      const relatedLines = lineItemRow.dataset.relatedLines;
      if (relatedLines) {
        for (const relatedItem of relatedLines.split(',')) {
          const relatedItemLine = Number(relatedItem);

          if (relatedItemLine && !isNaN(relatedItemLine)) {
            lines.push(relatedItemLine);
          }
        }
      }
    }

    if (quantity === 0) {
      return lines.length > 1 ? this.onLineItemsRemove(lines) : this.onLineItemRemove(line);
    }

    let newQuantity = quantity;

    if (lines.length > 1) {
      const itemRows = lines
        .map((l) => {
          const lineItemRow = this.refs.cartItemRows[l - 1];
          return lineItemRow ? lineItemRow.dataset.lineKey : null;
        })
        .filter(Boolean);

      this.updateQuantities({
        line,
        updates: itemRows.reduce((acc, lineKey) => {
          acc[lineKey] = newQuantity;
          return acc;
        }, {}),
        action: 'change',
      });
    } else {
      this.updateQuantity({
        line,
        quantity: newQuantity,
        action: 'change',
      });
    }

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector('text-component')
    );
    textComponent?.shimmer();
  }

  /** @param {number} line */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const lines = [line];
    const relatedLines = cartItemRowToRemove.dataset.relatedLines;
    if (relatedLines) {
      for (const relatedItem of relatedLines.split(',')) {
        const relatedItemLine = Number(relatedItem);

        if (relatedItemLine && !isNaN(relatedItemLine)) {
          lines.push(relatedItemLine);
        }
      }
    }

    if (lines.length > 1) {
      return this.onLineItemsRemove(lines);
    }

    const currentlyFocusedElement = document.activeElement;
    const isFocusedOnRemovedItem = cartItemRowToRemove.contains(currentlyFocusedElement);

    const removeButton = cartItemRowToRemove.querySelector('.cart-items__remove');
    const productTitle = removeButton?.dataset.productTitle || 'item';

    if (isFocusedOnRemovedItem) {
      const cartDialog = document.querySelector('dialog[aria-modal="true"]');

      if (cartDialog && cartDialog instanceof HTMLElement) {
        cartDialog.focus();
      } else {
        const cartSummary = document.querySelector('.cart__summary-totals');

        if (cartSummary && cartSummary instanceof HTMLElement) {
          cartSummary.focus();
        }
      }
    }

    this.pendingRemovalInfo = {
      productTitle,
      isFocusedOnRemovedItem,
      line,
    };

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const remove = () => {
      cartItemRowToRemove.remove();
    };

    if (prefersReducedMotion()) return remove();

    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');

    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /** @param {number[]} lines */
  onLineItemsRemove(lines) {
    const cartItemRowsToRemove = lines
      .map((line) => this.refs.cartItemRows[line - 1])
      .filter(Boolean);

    if (cartItemRowsToRemove.length === 0) return;

    const currentlyFocusedElement = document.activeElement;
    const isFocusedOnRemovedItem = cartItemRowsToRemove.some((row) =>
      row.contains(currentlyFocusedElement)
    );

    const removeButtons = cartItemRowsToRemove.map((row) =>
      row.querySelector('.cart-items__remove')
    );
    const productTitle = removeButtons[0]?.dataset.productTitle || 'item';

    if (isFocusedOnRemovedItem) {
      const cartDialog = document.querySelector('dialog[aria-modal="true"]');

      if (cartDialog && cartDialog instanceof HTMLElement) {
        cartDialog.focus();
      } else {
        const cartSummary = document.querySelector('.cart__summary-totals');

        if (cartSummary && cartSummary instanceof HTMLElement) {
          cartSummary.focus();
        }
      }
    }

    this.pendingRemovalInfo = {
      productTitle,
      isFocusedOnRemovedItem,
      line: Math.min(...lines),
    };

    this.updateQuantities({
      updates: cartItemRowsToRemove.reduce((acc, line) => {
        const lineKey = line.dataset.lineKey;
        if (lineKey) {
          acc[lineKey] = 0;
        }
        return acc;
      }, /** @type {Record<string, number>} */ ({})),
      line: lines[0],
      action: 'clear',
    });

    cartItemRowsToRemove.forEach((row) => {
      if (!row) return;

      const remove = () => {
        row.remove();
      };

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      onAnimationEnd(row, remove);
    });
  }

  /**
   * Switches a one-time line item to a subscription using its default selling plan.
   * @param {number} line - The 1-based line index.
   * @param {Event} event - The click event.
   */
  onLineItemSubscribe(line, event) {
    const target = event?.target;
    const button =
      target instanceof HTMLElement ? target.closest('.cart-items__plan-button') : null;
    const sellingPlanId = button instanceof HTMLElement ? button.dataset.defaultPlanId : null;

    if (!sellingPlanId) return;

    const row = this.refs.cartItemRows?.[line - 1];
    const loadingText = row?.querySelector('.cart-items__plan-switch')?.dataset.upgradeText || '';

    this.#changeSellingPlan({ line, sellingPlanId, loadingText });
  }

  /**
   * Handles the subscription frequency dropdown. An empty value reverts the line
   * item to a one-time purchase; any other value changes the selling plan.
   * @param {number} line - The 1-based line index.
   * @param {Event} event - The change event.
   */
  onLineItemPlanChange(line, event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const sellingPlanId = event.target.value;
    const row = this.refs.cartItemRows?.[line - 1];
    const planSwitch = row?.querySelector('.cart-items__plan-switch');

    if (sellingPlanId === '') {
      // "Deliver Once" selected - revert to a one-time purchase.
      const loadingText = planSwitch?.dataset.downgradeText || '';
      this.#changeSellingPlan({ line, sellingPlanId: null, loadingText });
    } else {
      // Change subscription frequency using the standard cart loading state.
      this.#changeSellingPlan({ line, sellingPlanId });
    }
  }

  /**
   * Swaps the active plan control for the disabled loading button with the given text.
   * @param {HTMLElement | undefined} row - The cart item row.
   * @param {string} text - The loading text to display.
   */
  #setPlanSwitchLoading(row, text) {
    const planSwitch = row?.querySelector('.cart-items__plan-switch');
    if (!planSwitch) return;

    const control = planSwitch.querySelector('[data-plan-control]');
    const loading = planSwitch.querySelector('[data-plan-loading]');
    const loadingText = planSwitch.querySelector('[data-plan-loading-text]');

    if (loadingText) loadingText.textContent = text;
    if (control instanceof HTMLElement) control.hidden = true;
    if (loading instanceof HTMLElement) loading.hidden = false;
  }

  /**
   * Changes (or removes) the selling plan of a cart line item via the cart change API.
   *
   * A BOGO line item is modelled as two cart lines (a "main" paid line and a free
   * partner line that carries a `_selling_plan_id` property to stay grouped with it),
   * so its selling plan transitions are delegated to {@link #changeBogoSellingPlan}
   * to keep both lines in sync.
   *
   * @param {Object} config - The config.
   * @param {number} config.line - The 1-based line index.
   * @param {string | null} config.sellingPlanId - The selling plan id, or null to revert to one-time.
   * @param {string} [config.loadingText] - When provided, the plan control is replaced with a loading button.
   */
  #changeSellingPlan(config) {
    const { line, sellingPlanId, loadingText } = config;

    const row = this.refs.cartItemRows?.[line - 1];
    const lineKey = row?.dataset.lineKey;

    if (!lineKey) return;

    const isBogo = row.dataset.bogo === '1';
    const sections = this.#getCartSectionIds();

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      'selling-plan:user-action'
    );
    this.#disableCartItems();

    if (loadingText) {
      this.#setPlanSwitchLoading(row, loadingText);
    }

    this.refs.cartTotal?.shimmer();

    const request = isBogo
      ? this.#changeBogoSellingPlan({ row, sellingPlanId, sections })
      : this.#postCartChange({
          id: lineKey,
          selling_plan: sellingPlanId,
          sections,
          sections_url: window.location.pathname,
        });

    request
      .then((parsed) => {
        resetShimmer(this);

        // No request was made (e.g. a BOGO row with no variant id). The loading swap
        // was already applied, so re-render to restore the original plan control.
        if (!parsed) {
          return sectionRenderer.renderSection(this.sectionId, { cache: false }).catch(() => {});
        }

        // The change API reports failures via `errors`; the add API (used for BOGO
        // re-adds) reports them via a truthy `status` with `message`/`description`.
        // Either way the section was not re-rendered, so route through the error
        // refresh to restore the plan control and surface the message.
        if (parsed.errors || parsed.status) {
          const currentQuantity = Number(row?.dataset.lineQuantity) || 1;
          return this.#handleCartErrorAndRefresh(
            line,
            currentQuantity,
            parsed.errors || parsed.description || parsed.message
          );
        }

        this.#applyCartSections(
          parsed,
          sellingPlanId ? 'Subscription updated. ' : 'Item changed to a one-time purchase. '
        );
      })
      .catch((error) => {
        console.error('Error changing cart selling plan:', error);
        // On failure the section is never morphed. For BOGO the cart may already
        // have been partially mutated (group cleared before a failed re-add), and
        // when a loading text was set the plan control is hidden behind the loading
        // button. Re-render unconditionally to re-sync the UI with server truth.
        sectionRenderer.renderSection(this.sectionId, { cache: false }).catch(() => {});
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Applies a selling plan transition to a BOGO group by rebuilding just that group
   * from scratch. The BOGO discount engine forces even quantities, re-allocates the
   * free discount, and re-keys line items on every change, so editing lines in place is
   * unreliable. The same variant can have several independent BOGO groups in the cart
   * (one-time plus one per subscription frequency), each made of a paid line and a free
   * partner sharing a `_selling_plan_id` grouping. We clear only the group being changed
   * and re-add the target state, mirroring the PDP add-to-cart, leaving sibling groups
   * untouched.
   *
   * @param {Object} config
   * @param {HTMLElement} config.row - The row whose control was used (the paid line).
   * @param {string | null} config.sellingPlanId - The selling plan id, or null to revert to one-time.
   * @param {string} config.sections - The comma separated section ids to render.
   * @returns {Promise<any>}
   */
  async #changeBogoSellingPlan(config) {
    const { row, sellingPlanId, sections } = config;
    const variantId = row.dataset.variantId;

    if (!variantId) return null;

    // The group of the clicked line, keyed by its current selling plan grouping
    // (the allocation id or `_selling_plan_id` property, blank for the one-time group).
    const groupPlanId = row.dataset.sellingPlanId || '';
    const totalQuantity =
      Number(row.dataset.totalQuantity) || Number(row.dataset.lineQuantity) || 2;
    // The group is "buy N get N"; pairs is N (the paid quantity). A settled BOGO
    // group always has an even total, but a transient odd total must round DOWN so
    // the re-add never puts back more items than were removed.
    const pairs = Math.max(1, Math.floor(totalQuantity / 2));
    const sectionsUrl = window.location.pathname;
    const targetPlanId = sellingPlanId || '';

    // Rebuild the original group from a clean slate so the customer never silently
    // loses the BOGO items. This is idempotent and safe after ANY partial failure
    // (a clear that stopped mid-way, or a partial/failed re-add): it first removes any
    // remnants of both the target and the original group key, then re-adds the original
    // state. A failed restore is logged rather than ignored; the caller re-renders the
    // cart to server truth either way, so the customer sees the real state plus an error.
    const restoreOriginalGroup = async () => {
      try {
        await this.#clearBogoGroup(variantId, targetPlanId);
        if (String(groupPlanId) !== String(targetPlanId)) {
          await this.#clearBogoGroup(variantId, groupPlanId);
        }
        const restored = await this.#postCartAdd({
          items: this.#buildBogoItems(variantId, groupPlanId || null, pairs),
          sections,
          sections_url: sectionsUrl,
        });
        if (restored?.errors || restored?.status) {
          throw new Error(restored.errors || restored.description || restored.message);
        }
      } catch (rollbackError) {
        console.error('Failed to restore BOGO group:', rollbackError);
      }
    };

    // Clear the existing group. If it fails after removing some lines, restore the
    // original state before surfacing the error.
    try {
      await this.#clearBogoGroup(variantId, groupPlanId);
    } catch (clearError) {
      await restoreOriginalGroup();
      throw clearError;
    }

    let result;
    try {
      result = await this.#postCartAdd({
        items: this.#buildBogoItems(variantId, sellingPlanId, pairs),
        sections,
        sections_url: sectionsUrl,
      });
    } catch (error) {
      // Re-add threw (network/parse). Best-effort restore, then re-throw the original
      // error so #changeSellingPlan re-renders the cart to server truth.
      await restoreOriginalGroup();
      throw error;
    }

    // Re-add reported an error status: restore the original group, then return the
    // failed result so #changeSellingPlan routes through the error refresh (which
    // re-syncs the section and surfaces the message).
    if (result?.errors || result?.status) {
      await restoreOriginalGroup();
    }

    return result;
  }

  /**
   * Builds the cart-add line items for a BOGO group in the given plan state.
   * @param {string} variantId - The BOGO variant id.
   * @param {string | null} sellingPlanId - The selling plan id, or null for one-time.
   * @param {number} pairs - The paid quantity N of the "buy N get N" group.
   * @returns {Array<Object>}
   */
  #buildBogoItems(variantId, sellingPlanId, pairs) {
    if (sellingPlanId) {
      // Subscription BOGO: a paid subscription line plus a free one-time partner that
      // share identical properties and differ only by the selling plan.
      return [
        {
          id: variantId,
          quantity: pairs,
          selling_plan: sellingPlanId,
          properties: { _bogo: '1', _selling_plan_id: sellingPlanId },
        },
        {
          id: variantId,
          quantity: pairs,
          properties: { _bogo: '1', _selling_plan_id: sellingPlanId },
        },
      ];
    }

    // One-time BOGO: a single line carrying the full (paid + free) quantity; the
    // discount engine splits it into the paid line and its free partner.
    return [{ id: variantId, quantity: pairs * 2, properties: { _bogo: '1' } }];
  }

  /**
   * Removes the cart lines of a single BOGO group for a variant, leaving any other
   * groups for the same variant intact. A group is the paid line plus its free partner,
   * identified by their shared selling plan grouping: subscription groups share a
   * `_selling_plan_id` property equal to the plan id, the one-time group has `_bogo`
   * with no `_selling_plan_id`. The cart re-keys lines on each change, so each
   * removal's response feeds the next lookup; the loop is bounded to avoid spinning.
   * @param {string} variantId - The BOGO variant id.
   * @param {string} groupPlanId - The group's plan id, or '' for the one-time group.
   * @returns {Promise<void>}
   */
  async #clearBogoGroup(variantId, groupPlanId) {
    const belongsToGroup = (item) => {
      if (String(item.variant_id) !== String(variantId)) return false;
      if (!item.properties?._bogo) return false;
      // Mirror the group key the row exposes (cart-products.liquid): the selling
      // plan allocation id, falling back to the `_selling_plan_id` property.
      const linePlanId =
        item.selling_plan_allocation?.selling_plan?.id || item.properties?._selling_plan_id || '';
      return String(linePlanId) === String(groupPlanId);
    };

    // Seed from the cart once, then reuse the change API's response (which returns
    // the re-keyed cart) for the next iteration instead of re-fetching every time.
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { 'Cache-Control': 'no-cache' },
    });
    let cart = await response.json();

    for (let i = 0; i < 20; i++) {
      const line = cart.items?.find(belongsToGroup);

      if (!line) return;

      const result = await this.#postCartChange({ id: line.key, quantity: 0 });
      // Abort if the removal failed; otherwise the caller would re-add on top of
      // the still-present group and duplicate the BOGO quantities.
      if (result?.errors || result?.status) {
        throw new Error(
          `Failed to clear BOGO group: ${result.errors || result.description || result.message}`
        );
      }
      // cart/change.js returns the updated cart, so no extra fetch is needed.
      cart = result;
    }

    // The group should have cleared within a few iterations; exhausting the loop
    // means a line keeps reappearing. Abort rather than re-adding on top of it.
    throw new Error(`Could not clear BOGO group for variant ${variantId} after 20 attempts`);
  }

  /**
   * Builds the comma separated list of cart section ids that need re-rendering.
   * @returns {string}
   */
  #getCartSectionIds() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    return Array.from(sectionsToUpdate).join(',');
  }

  /**
   * POSTs a single change to the cart change API and returns the parsed JSON response.
   * @param {Object} body - The change request body.
   * @returns {Promise<any>}
   */
  async #postCartChange(body) {
    const response = await fetch(
      `${Theme.routes.cart_change_url}`,
      fetchConfig('json', { body: JSON.stringify(body) })
    );
    return JSON.parse(await response.text());
  }

  /**
   * POSTs to the cart add API and returns the parsed JSON response.
   * @param {Object} body - The add request body.
   * @returns {Promise<any>}
   */
  async #postCartAdd(body) {
    const response = await fetch(
      `${Theme.routes.cart_add_url}`,
      fetchConfig('json', { body: JSON.stringify(body) })
    );
    return JSON.parse(await response.text());
  }

  /**
   * Renders the cart sections from a parsed cart response: morphs this section,
   * dispatches the cart update, keeps the drawer open and refreshes wallet buttons.
   * @param {any} parsed - The parsed cart change/add response containing `sections`.
   * @param {string} [announcement] - Optional screen-reader message to prepend to the
   *   cart live region so it is heard once the morphed section is in the DOM.
   */
  #applyCartSections(parsed, announcement = '') {
    let sectionHtml = parsed.sections?.[this.sectionId];
    if (!sectionHtml) return;

    const newSectionHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');

    // Grab the new cart item count from a hidden element
    const newCartHiddenItemCount =
      newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount
      ? Number.parseInt(newCartHiddenItemCount, 10)
      : 0;

    this.dispatchEvent(
      new CartUpdateEvent({}, this.sectionId, {
        itemCount: newCartItemCount,
        source: 'cart-items-component',
        sections: parsed.sections,
      })
    );

    // Prepend the announcement to the live region so assistive tech hears it after
    // the section is morphed in. Reuse the DOM already parsed for the item count.
    if (announcement) {
      const liveRegion = newSectionHTML.querySelector('[ref="cartAnnouncement"]');
      if (liveRegion) {
        liveRegion.textContent = announcement + (liveRegion.textContent || '');
        sectionHtml = newSectionHTML.body.innerHTML;
      }
    }

    // Capture cart drawer dialog state before morphing
    const cartDrawerDialog = /** @type {HTMLDialogElement | null} */ (
      document.querySelector('cart-drawer-component dialog[ref="dialog"]')
    );
    const wasDialogOpen = cartDrawerDialog?.open ?? false;

    morphSection(this.sectionId, sectionHtml);

    // Restore cart drawer dialog if it was open and cart still has items
    if (wasDialogOpen && newCartItemCount > 0) {
      const dialog = /** @type {HTMLDialogElement | null} */ (
        document.querySelector('cart-drawer-component dialog[ref="dialog"]')
      );
      if (dialog && !dialog.open) {
        dialog.showModal();
      }
    }

    this.#updateAdditionalCheckoutButtons(sectionHtml);

    setTimeout(() => {
      this.#checkCartAndUpdatePaypalButtons();
      this.#restoreIdmeVerification();
      this.#toggleWishlistButtonClass();
    }, 150);
  }

  /**
   * Finds the next focusable element after a cart item row is removed: the first focusable
   * element of the row that took the removed row's place (or the last row, if the removed row
   * was the last one).
   * @param {number} [removedLine] - The 1-based line of the removed row.
   * @returns {HTMLElement|null} The next focusable element.
   */
  findNextFocusableElement(removedLine = 1) {
    const cartItemRows = this.querySelectorAll('[ref="cartItemRows[]"]');
    const nextCartItemRow = cartItemRows[Math.min(removedLine, cartItemRows.length) - 1];

    if (nextCartItemRow) {
      const focusableElement = nextCartItemRow.querySelector(
        'button:not([disabled]), input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElement && focusableElement instanceof HTMLElement) {
        return focusableElement;
      }
    }

    const { closeButton } = this.refs;
    if (closeButton && closeButton instanceof HTMLElement) {
      return closeButton;
    }

    return null;
  }

  /**
   * Manages focus after removal. Never scrolls: removing an item must leave the page where the
   * customer was, and the focused row sits next to the one they just removed.
   * @param {HTMLElement|null} nextFocusableElement - The next focusable element.
   */
  manageFocusAfterRemoval(nextFocusableElement) {
    if (nextFocusableElement) {
      nextFocusableElement.focus({ preventScroll: true });
    }
  }

  handleRemovalAnnouncement() {
    if (!this.pendingRemovalInfo) return;

    const { productTitle } = this.pendingRemovalInfo;

    const { cartAnnouncement } = this.refs;

    if (cartAnnouncement) {
      const currentText = cartAnnouncement.textContent || '';
      const removalMessage = `${productTitle} removed from cart. `;

      cartAnnouncement.textContent = '';
      setTimeout(() => {
        cartAnnouncement.textContent = removalMessage + currentText;
      }, 10);
    }

    this.pendingRemovalInfo = null;
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`
    );
    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: this.#getCartSectionIds(),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          const requestedQuantity = Number(quantity);
          return this.#handleCartErrorAndRefresh(
            line,
            requestedQuantity,
            parsedResponseText.errors
          );
        }

        let announcement = '';
        if (config.action === 'clear' && this.pendingRemovalInfo) {
          announcement = `${this.pendingRemovalInfo.productTitle} removed from cart. `;
        } else if (config.action === 'change') {
          const productTitle = this.refs.cartItemRows?.[line - 1]?.dataset.productTitle || 'item';
          announcement = `Quantity of ${productTitle} was updated. `;
        }

        this.#applyCartSections(parsedResponseText, announcement);

        if (config.action === 'clear' && this.pendingRemovalInfo) {
          const { isFocusedOnRemovedItem, line: removedLine } = this.pendingRemovalInfo;

          if (isFocusedOnRemovedItem) {
            this.manageFocusAfterRemoval(this.findNextFocusableElement(removedLine));
          }

          this.pendingRemovalInfo = null;
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Updates the quantities. ONLY USE THIS IF IT IS ABSOLUTELY NECESSARY TO UPDATE MULTIPLE QUANTITIES AT ONCE.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {Object} config.updates - The updates.
   * @param {string} config.action - The action.
   */
  updateQuantities(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`
    );
    this.#disableCartItems();

    const { line, updates } = config;
    const { cartTotal } = this.refs;

    const body = JSON.stringify({
      updates,
      sections: this.#getCartSectionIds(),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          const firstRequestedQuantity = Number(Object.values(updates)[0] || 0);
          return this.#handleCartErrorAndRefresh(
            line,
            firstRequestedQuantity,
            parsedResponseText.errors
          );
        }

        let announcement = '';
        if (config.action === 'clear' && this.pendingRemovalInfo) {
          announcement = `${this.pendingRemovalInfo.productTitle} removed from cart. `;
        } else if (config.action === 'change') {
          const productTitle = this.refs.cartItemRows?.[line - 1]?.dataset.productTitle || 'item';
          announcement = `Quantity of ${productTitle} was updated. `;
        }

        this.#applyCartSections(parsedResponseText, announcement);

        if (config.action === 'clear' && this.pendingRemovalInfo) {
          const { isFocusedOnRemovedItem, line: removedLine } = this.pendingRemovalInfo;

          if (isFocusedOnRemovedItem) {
            this.manageFocusAfterRemoval(this.findNextFocusableElement(removedLine));
          }

          this.pendingRemovalInfo = null;
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) return;
    if (!(cartItemErrorContainer instanceof HTMLElement)) return;

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('!hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      const resource = event.detail.resource;
      const sectionHtml = resource?.sections?.[this.sectionId];
      if (sectionHtml) {
        morphSection(this.sectionId, sectionHtml);
        this.#updateAdditionalCheckoutButtons(sectionHtml);
      } else {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      }

      setTimeout(() => {
        this.#checkCartAndUpdatePaypalButtons();
        this.#restoreIdmeVerification();
        this.#toggleWishlistButtonClass();
      }, 150);
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateAdditionalCheckoutButtons(cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    setTimeout(() => {
      this.#checkCartAndUpdatePaypalButtons();
      this.#restoreIdmeVerification();
      this.#toggleWishlistButtonClass();
    }, 150);
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #correctBogoQuantities() {
    const cartItemRows = this.refs.cartItemRows;

    if (!cartItemRows) return;

    cartItemRows.forEach((row, line) => {
      const isBogo = row.dataset.bogo === '1';
      if (!isBogo) return;

      const isMainItem = row.dataset.mainItem === 'true';

      if (isMainItem) return;

      const sellingPlanId = row.dataset.sellingPlanId;
      const isBogoFreeItem = row.dataset.bogoFreeLine === 'true';

      if (sellingPlanId && !isBogoFreeItem) {
        // if it has selling plan but it is not main item or free item, then BOGO discount didn't apply to it and it should be removed
        this.updateQuantity({
          line: line + 1,
          quantity: 0,
          action: 'clear',
        });
        return;
      }

      const totalQuantity = row.dataset.totalQuantity;
      const lineQuantity = row.dataset.lineQuantity;

      // Only correct if isBogo and total quantity is odd and line quantity is less than half of total quantity
      if (
        isBogo &&
        totalQuantity &&
        parseInt(totalQuantity, 10) % 2 !== 0 &&
        lineQuantity &&
        parseInt(lineQuantity, 10) < Math.ceil(parseInt(totalQuantity, 10) / 2)
      ) {
        this.updateQuantity({
          line: line + 1,
          quantity: parseInt(lineQuantity, 10) + 1,
          action: 'change',
        });
      }
    });
  }

  #toggleWishlistButtonClass() {
    const wishlistButtons = document.querySelectorAll('.product-card-wishlist-button');
    if (wishlistButtons.length === 0) return;

    const originalValues = new Map();
    wishlistButtons.forEach((button) => {
      const originalValue = button.getAttribute('data-product-handle');
      if (originalValue) {
        originalValues.set(button, originalValue);
      }
    });

    setTimeout(() => {
      wishlistButtons.forEach((button) => {
        const originalValue = originalValues.get(button);
        if (originalValue) {
          button.setAttribute('data-product-handle', originalValue);
        }
      });
    }, 100);
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
